import type {
  FileEntryDomainContractDto,
  FileSiteFileEntryDomainContractDto,
} from "../types/FileEntry";
import type { MaybeRef } from "vue";
import { toast } from "vue-sonner";
import { useResourceDefinitions } from "./useResourceDefinitions";

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  pageCount: number;
  pageSize: number;
  pageNumber: number;
}

export interface ChildrenPage {
  items: FileSiteFileEntryDomainContractDto[];
  totalCount: number;
  pageCount: number;
  pageSize: number;
  pageNumber: number;
}

function handleApiError(err: any) {
  const status = err?.status || err?.response?.status;
  const message =
    err?.data?.detail ||
    err?.data?.title ||
    err?.response?._data?.detail ||
    err?.response?._data?.title ||
    err?.message ||
    "API request failed";

  if (status === 409) {
    toast.error("An entry with that name already exists.");
  } else {
    toast.error(message);
  }
}

function isNotFoundError(err: any) {
  const status =
    err?.statusCode ??
    err?.status ??
    err?.response?.status ??
    err?.data?.statusCode ??
    err?.data?.status;
  return status === 404;
}

export function useFileEntries(siteId: MaybeRef<string | null | undefined>) {
  const { isResourceDefinitionType } = useResourceDefinitions();
  const { site } = useSite();
  const loading = ref(false);
  const uploading = ref(false);
  const error = ref<Error | null>(null);
  const childrenPaging = ref<{
    totalCount: number;
    pageCount: number;
    pageSize: number;
    pageNumber: number;
  }>({ totalCount: 0, pageCount: 0, pageSize: 20, pageNumber: 1 });

  const resolveSiteId = () => {
    const provided = unref(siteId);
    const resolved =
      (typeof provided === "string" ? provided : "") || site.value?.id || "";
    if (resolved === "undefined" || resolved === "null") {
      return "";
    }
    return resolved;
  };

  const getSiteHeader = () => {
    const resolvedSiteId = resolveSiteId();
    return resolvedSiteId ? { "x-site-id": resolvedSiteId } : {};
  };

  /**
   * Fetch only the entry itself, without loading child entries.
   */
  async function getEntry(
    path = "",
    include: string = "metadata",
    options: { suppressNotFoundToast?: boolean } = {}
  ): Promise<FileEntryDomainContractDto | null> {
    loading.value = true;
    error.value = null;

    try {
      const { data, error: entryError } =
        await useApi<FileEntryDomainContractDto>(`/v1/file/${path}`, {
          headers: {
            ...getSiteHeader(),
            "x-include-domain-contracts": include,
          },
        });

      if (entryError.value) {
        throw entryError.value;
      }

      return data.value || null;
    } catch (err: any) {
      error.value = err;
      if (!(options.suppressNotFoundToast && isNotFoundError(err))) {
        handleApiError(err);
      }
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch a single page of child entries under a given path.
   */
  async function getChildren(
    path = "",
    opts: { pageNo?: number; pageSize?: number; include?: string } = {}
  ): Promise<ChildrenPage> {
    const { pageNo = 1, pageSize = 20, include = "metadata" } = opts;

    const { data, error: childrenError } = await useApi<
      PagedResult<FileSiteFileEntryDomainContractDto>
    >(`/v1/file/children/${path}`, {
      query: { pageNo, pageSize },
      headers: {
        ...getSiteHeader(),
        "x-include-domain-contracts": include,
      },
    });

    if (childrenError.value) {
      throw childrenError.value;
    }

    const result = data.value;
    return {
      items: result?.data ?? [],
      totalCount: result?.totalCount ?? 0,
      pageCount: result?.pageCount ?? 0,
      pageSize: result?.pageSize ?? pageSize,
      pageNumber: result?.pageNumber ?? pageNo,
    };
  }

  /**
   * Fetch a folder entry together with its children.
   *
   * When an explicit `pageNo`/`pageSize` is supplied a single page is returned;
   * otherwise every page is aggregated so existing (non-paged) consumers such as
   * the file pickers continue to see all children.
   */
  async function get(
    path = "",
    include: string = "metadata",
    options: { suppressNotFoundToast?: boolean; pageNo?: number; pageSize?: number } = {}
  ): Promise<FileEntryDomainContractDto | null> {
    const entry = ref<FileEntryDomainContractDto | null>(null);
    loading.value = true;
    error.value = null;

    try {
      const { data: fileData, error: fileError } =
        await useApi<FileEntryDomainContractDto>(`/v1/file/${path}`, {
          headers: {
            ...getSiteHeader(),
            "x-include-domain-contracts": include,
          },
        });

      if (fileError.value) {
        throw fileError.value;
      }

      const explicitPaging =
        options.pageNo !== undefined || options.pageSize !== undefined;

      let children: FileSiteFileEntryDomainContractDto[] = [];
      let lastPage: ChildrenPage;

      if (explicitPaging) {
        lastPage = await getChildren(path, {
          pageNo: options.pageNo ?? 1,
          pageSize: options.pageSize ?? childrenPaging.value.pageSize,
          include,
        });
        children = lastPage.items;
      } else {
        const aggregateSize = 100;
        let currentPage = 1;
        do {
          lastPage = await getChildren(path, {
            pageNo: currentPage,
            pageSize: aggregateSize,
            include,
          });
          children.push(...lastPage.items);
          currentPage++;
        } while (currentPage <= lastPage.pageCount);
      }

      entry.value = fileData.value || null;
      if (entry.value) {
        entry.value.children = children;
      }
      childrenPaging.value = {
        totalCount: lastPage.totalCount,
        pageCount: lastPage.pageCount,
        pageSize: lastPage.pageSize,
        pageNumber: lastPage.pageNumber,
      };
    } catch (err: any) {
      error.value = err;
      if (!(options.suppressNotFoundToast && isNotFoundError(err))) {
        handleApiError(err);
      }
    } finally {
      loading.value = false;
    }

    return entry.value;
  }

  /**
   * Create a new entry under `path`.
   */
  async function create(
    path: string,
    file: File | null = null,
    options: { filename?: string; mkdir?: boolean } = {}
  ): Promise<void> {
    uploading.value = true;
    error.value = null;
    // Match assessment answer behavior: auto-create path unless explicitly disabled.
    const shouldMkdir = options.mkdir ?? true;
    const mkdirHeader = shouldMkdir ? { "x-mkdir": true } : {};

    const { requestApiUrl } = useApiUrl();

    try {
      // 1) Folder Creation (no file)
      if (!file) {
        const parts = path.split("/");
        const folderName = parts.pop() || "";
        const parentPath = parts.join("/");
        const url = parentPath.length > 0 ? `/v1/file/${parentPath}` : `/v1/file/`;

        console.log(`FOLDER → POST ${url}  (folder="${folderName}")`);

        await useApiFetch(url, {
          method: "POST",
          headers: {
            ...getSiteHeader(),
            "Content-Disposition": `form-data; name="folder"; filename="${folderName}"`,
            "Content-Type": "text/directory",
            ...mkdirHeader,
          },
        });

        return;
      }

      // 2) ResourceDefinition
      if (isResourceDefinitionType(file.type)) {
        // always post to the folder path; filename comes from the File object
        const parentPath = path;
        const url = parentPath ? `/v1/file/${parentPath}` : `/v1/file/`;
        console.log(`RESOURCE-DEFINITION → POST ${url}`);

        await useApiFetch(url, {
          method: "POST",
          headers: {
            ...getSiteHeader(),
            "Content-Disposition": `form-data; name="resourcedefinition"; filename="${file.name}"`,
            "Content-Type": file.type,
            ...mkdirHeader,
          },
          body: file,
        });

        return;
      }

      // 3) Asset (binary) upload
      const assetUrl = `/v1/file/${path}`;
      const filename = options.filename ?? file.name;
      console.log(`ASSET → POST ${requestApiUrl.value + assetUrl}  (file="${filename}")`);

      await useApiFetch(assetUrl, {
        method: "POST",
        headers: {
          ...getSiteHeader(),
          "Content-Disposition": `form-data; name="file"; filename="${filename}"`,
          "Content-Type": file.type,
          ...mkdirHeader,
        },
        body: file,
      });
    } catch (err: any) {
      error.value = err;
      handleApiError(err);
      throw err;
    } finally {
      uploading.value = false;
    }
  }

  return {
    loading,
    uploading,
    error,
    childrenPaging,
    getEntry,
    get,
    getChildren,
    create,
  };
}
